import React from "react";
import { Flex, Stack, Text, Select, Switch, Tooltip, useColorModeValue } from "@chakra-ui/react";
import { getTypeLabel } from "./legalDocumentConstants";
import { LANGUAGE_OPTIONS } from "./legalFormStyles";

/**
 * Renders the language filter dropdown (French selected by default).
 * @param {object} props
 * @param {string} props.filterLanguage - Currently selected language filter
 * @param {function} props.setFilterLanguage - Setter for language filter
 * @returns {React.ReactElement}
 */
function LanguageFilter({ filterLanguage, setFilterLanguage }) {
    return (
        <Select size="sm" borderRadius="10px" minW="110px" w="110px" value={filterLanguage}
            onChange={(e) => setFilterLanguage(e.target.value)}>
            {LANGUAGE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </Select>
    );
}

/**
 * Renders a toggle switch to filter between all versions and latest only.
 * @param {object} props
 * @param {boolean} props.latestOnly - Current toggle state
 * @param {function} props.setLatestOnly - Setter for toggle state
 * @returns {React.ReactElement}
 */
function LatestToggle({ latestOnly, setLatestOnly }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Tooltip label={latestOnly ? "Showing latest versions only" : "Showing all versions"}>
            <Stack direction="row" align="center" spacing="6px">
                <Text fontSize="0.72rem" color={labelColor}>Latest</Text>
                <Switch size="sm" colorScheme="brand" isChecked={latestOnly}
                    onChange={(e) => setLatestOnly(e.target.checked)} />
            </Stack>
        </Tooltip>
    );
}

/**
 * Renders filter controls for document type, language, and latest-only toggle.
 * @param {object} props
 * @param {Array} props.types - Available document type options
 * @param {string} props.filterType - Currently selected type filter
 * @param {function} props.setFilterType - Setter for type filter
 * @param {string} props.filterLanguage - Currently selected language filter
 * @param {function} props.setFilterLanguage - Setter for language filter
 * @param {boolean} props.latestOnly - Whether to show latest versions only
 * @param {function} props.setLatestOnly - Setter for latest-only toggle
 * @returns {React.ReactElement}
 */
function ListFilterBar({ types, filterType, setFilterType, filterLanguage, setFilterLanguage, latestOnly, setLatestOnly }) {
    return (
        <Flex gap="8px" align="center" mb="16px" wrap="wrap">
            <Select size="sm" borderRadius="10px" flex="1" minW="140px" value={filterType}
                onChange={(e) => setFilterType(e.target.value)}>
                <option value="">All types</option>
                {types.map((t) => <option key={t} value={t}>{getTypeLabel(t)}</option>)}
            </Select>
            <LanguageFilter filterLanguage={filterLanguage} setFilterLanguage={setFilterLanguage} />
            <LatestToggle latestOnly={latestOnly} setLatestOnly={setLatestOnly} />
        </Flex>
    );
}

export default ListFilterBar;
